module Client.Domain.Navbar

open Client.DomainTypes
open Client.DomainTypes.Msg
open Client.Infrastructure
open Fable.React
open Fable.React.Props
open Shared.Dto.User
open Fulma
open Client.Domain
open Fable.FontAwesome
open Browser

type DropDownState =
    | Open
    | Closed

let isDropDownOpen (state: DropDownState) =
    match state with
    | Open -> true
    | Closed -> false

let toggleDropDownState (state: DropDownState) : DropDownState =
    match state with
    | Open -> Closed
    | Closed -> Open

type NavbarModel = {
    Burger: DropDownState
    Administration: DropDownState
    PestModels: DropDownState
    ExternalSystems: DropDownState
    Help: DropDownState
    Account: DropDownState
}

let initialNavbarModel: NavbarModel = {
    Burger = Closed
    Administration = Closed
    PestModels = Closed
    ExternalSystems = Closed
    Help = Closed
    Account = Closed
}

let updateModel (msg: NavbarMsg) (model: NavbarModel) : NavbarModel =
    match msg with
    | ToggleBurger ->
        if isDropDownOpen model.Burger then
            initialNavbarModel
        else
            { model with Burger = Open }
    | TogglePestModels -> { model with PestModels = toggleDropDownState model.PestModels }
    | ToggleAccount -> { model with Account = toggleDropDownState model.Account }
    | ToggleAdministration -> { model with Administration = toggleDropDownState model.Administration }
    | ToggleHelp -> { model with Help = toggleDropDownState model.Help }
    | ToggleExternalSystems -> { model with ExternalSystems = toggleDropDownState model.ExternalSystems }

let private toggleNavbarOnClick dispatch (msg: NavbarMsg) =
    let f = fun _ -> msg |> Navbar |> Global |> dispatch

    Clickable.onClickPreventDefault f |> OnClick

let private navbarProps dispatch (route: Route) : IHTMLProp list = [
    Clickable.onClickGoToRoute dispatch route |> OnClick
    Href(Routing.routeToUrl route)
]

let adminMenu dispatch (model: NavbarModel) (user: LoggedInUserDto) =
    let navbarProps = navbarProps dispatch

    match user.Role with
    | Admin ->
        let item =
            Navbar.Item.div [
                Navbar.Item.HasDropdown
                Navbar.Item.IsHoverable
                Navbar.Item.IsActive(isDropDownOpen model.Administration)
            ] [
                Navbar.Link.a [
                    Navbar.Link.Props [
                        toggleNavbarOnClick dispatch NavbarMsg.ToggleAdministration
                    ]
                ] [
                    Icon.icon [] [ Fa.i [ Fa.Solid.Tools ] [] ]
                    span [] [ str "Administration" ]
                ]
                Navbar.Dropdown.div [] [
                    Navbar.Item.a [
                        Navbar.Item.Props(navbarProps Route.TtnSensorList)
                    ] [ str "Physische Sensoren" ]
                    Navbar.Item.a [
                        Navbar.Item.Props(navbarProps Route.MySensSensorList)
                    ] [ str "Karten Sensoren" ]
                    Navbar.Item.a [
                        Navbar.Item.Props(navbarProps Route.GatewayList)
                    ] [ str "Gateways" ]
                    Navbar.Item.a [ Navbar.Item.Props(navbarProps Route.UserList) ] [ str "Benutzer" ]
                    Navbar.Item.a [ Navbar.Item.Props(navbarProps Route.GroupList) ] [ str "Benutzergruppen" ]
                    Navbar.Item.a [
                        Navbar.Item.Props(navbarProps Route.AlertsList)
                    ] [ str "Aktive Bewarnungen" ]
                    Navbar.Item.a [
                        Navbar.Item.Props(navbarProps Route.CalculationConfiguration)
                    ] [ str "Konfiguration Berechnungsmodelle" ]
                ]
            ]

        Some item
    | Role.RegularUser
    | Role.TestUser -> None

let helpMenu dispatch (model: NavbarModel) =
    Navbar.Item.div [
        Navbar.Item.HasDropdown
        Navbar.Item.IsHoverable
        Navbar.Item.IsActive(isDropDownOpen model.Help)
    ] [
        Navbar.Link.a [
            Navbar.Link.Props [
                toggleNavbarOnClick dispatch NavbarMsg.ToggleHelp
            ]
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.QuestionCircle ] [] ]
            span [ classList [ "is-hidden-desktop", true ] ] [ str "Hilfe und Support" ]
        ]
        Navbar.Dropdown.div [ Navbar.Dropdown.IsRight ] [
            Navbar.Item.a [
                Navbar.Item.Props [
                    Href "https://mysens.eu/anfrage/"
                    Target "_blank"
                ]
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.Envelope ] [] ]
                span [] [ str "Support Anfrage" ]
            ]
            Navbar.Item.a [
                Navbar.Item.Props [
                    Href "https://mysens.eu/mysens-faq_4738ug519825dxis677s87gd628jw99o1/"
                    Target "_blank"
                ]
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.InfoCircle ] [] ]
                span [] [ str "Hilfe" ]
            ]
            Navbar.Item.a [
                Navbar.Item.Props [ OnClick(fun _ -> window.location.reload true) ]
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.Sync ] [] ]
                span [] [ str "Gesamte Webseite neu laden" ]
            ]
        ]
    ]

let private pestModelsNavbar dispatch (model: NavbarModel) (user: LoggedInUserDto) =
    let navbarProps = navbarProps dispatch

    Navbar.Item.div [
        Navbar.Item.HasDropdown
        Navbar.Item.IsHoverable
        Navbar.Item.IsActive(isDropDownOpen model.PestModels)
    ] [
        Navbar.Link.a [
            Navbar.Link.Props [
                toggleNavbarOnClick dispatch NavbarMsg.TogglePestModels
            ]
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Bug ] [] ]
            span [] [ str "Schadmodelle" ]
        ]
        Navbar.Dropdown.div [] [
            Navbar.Item.a [ Navbar.Item.Props(navbarProps Route.RimPro) ] [ str "RimPro" ]

            Navbar.Item.a [
                Navbar.Item.Props(navbarProps Route.MyScabStations)
            ] [ str "MySens Schorf" ]
            Navbar.Item.a [
                Navbar.Item.Props(navbarProps Route.MyPeronosporaStations)
            ] [ str "MySens Peronospora" ]
            Navbar.Item.a [
                Navbar.Item.Props(navbarProps Route.MyTipBurnStations)
            ] [ str "MySens TipBurn" ]
        ]
    ]

let private externalSystemsNavbar dispatch (model: NavbarModel) =
    let navbarProps = navbarProps dispatch

    Navbar.Item.div [
        Navbar.Item.HasDropdown
        Navbar.Item.IsHoverable
        Navbar.Item.IsActive(isDropDownOpen model.ExternalSystems)
    ] [
        Navbar.Link.a [
            Navbar.Link.Props [
                toggleNavbarOnClick dispatch NavbarMsg.ToggleExternalSystems
            ]
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.NetworkWired ] [] ]
            span [] [ str "Externe Steuerungen" ]
        ]

        Navbar.Dropdown.div [] [
            Navbar.Item.a [
                Navbar.Item.Props(navbarProps Route.MyTalgilTriggers)
            ] [ str "Talgil" ]
        ]
    ]

let navbarStart dispatch (model: NavbarModel) (user: LoggedInUserDto) =
    let navbarProps = navbarProps dispatch

    [
        match adminMenu dispatch model user with
        | Some adminDropDown -> adminDropDown
        | None -> ()

        Navbar.Item.a [
            Navbar.Item.Props(navbarProps (Route.SensorMap None))
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.MapMarkedAlt ] [] ]
            span [] [ str "Karte" ]
        ]
        Navbar.Item.a [
            Navbar.Item.Props(navbarProps Route.MySensorsData)
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.ChartLine ] [] ]
            span [] [ str "Messwerte" ]
        ]
        Navbar.Item.a [ Navbar.Item.Props(navbarProps Route.MyAlerts) ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.ExclamationTriangle ] [] ]
            span [] [ str "Bewarnung" ]
        ]

        pestModelsNavbar dispatch model user

        externalSystemsNavbar dispatch model

        Navbar.Item.a [
            Navbar.Item.Props(navbarProps Route.MyGateways)
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.BroadcastTower ] [] ]
            span [] [ str "Empfänger" ]
        ]
        Navbar.Item.a [
            Navbar.Item.Props(navbarProps Route.MyMapSensors)
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Cog ] [] ]
            span [] [ str "Sensoren" ]
        ]
        Navbar.Item.a [
            Navbar.Item.Props(navbarProps Route.MyUserGroups)
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Users ] [] ]
            span [] [ str "Gruppen" ]
        ]
    ]

let private accountMenu dispatch (model: NavbarModel) (user: LoggedInUserDto) =
    Navbar.Item.div [
        Navbar.Item.HasDropdown
        Navbar.Item.IsHoverable
        Navbar.Item.IsActive(isDropDownOpen model.Account)
    ] [
        Navbar.Link.a [
            Navbar.Link.Props [
                toggleNavbarOnClick dispatch NavbarMsg.ToggleAccount
            ]
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.User ] [] ]
            span [ classList [ "is-hidden-desktop", true ] ] [ str "Account-Einstellungen" ]
        ]
        Navbar.Dropdown.div [ Navbar.Dropdown.IsRight ] [
            Navbar.Item.div [ Navbar.Item.CustomClass "logged-in" ] [
                p [] [ str "Angemeldet als:" ]
                p [ Style [ FontWeight "bold" ] ] [ str (loggedInUserFullName user) ]

                match user.OriginalUser with
                | Some _ ->
                    a [
                        Clickable.onClickPreventDefault (fun _ -> dispatch (Global(GlobalMsg.StopImpersonation)))
                        |> OnClick
                    ] [ str "Benutzeransicht beenden" ]
                | None -> ()
            ]
            Navbar.Item.a [
                Navbar.Item.Props(navbarProps dispatch Route.MyProfile)
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.User ] [] ]
                span [] [ str "Mein Account" ]
            ]

            Navbar.Item.div [] [
                Button.button [
                    Button.Color IsGrey
                    Button.OnClick(fun _ -> LogoutPressed |> Global |> dispatch)
                ] [
                    Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.SignOutAlt ] [] ]
                    span [] [ str "Ausloggen" ]
                ]
            ]
        ]
    ]

let navbar dispatch (model: NavbarModel) (user: LoggedInUserDto) =
    Navbar.navbar [
        Navbar.Color IsLight
        Navbar.CustomClass "main-nav"
    ] [
        Navbar.Brand.div [] [
            Navbar.Item.a [
                Navbar.Item.Props [
                    Clickable.onClickGoToRoute dispatch Route.Home |> OnClick
                ]
            ] [ img [ Src "/mysens_logo.png" ] ]
            Navbar.burger [
                Navbar.Burger.OnClick(fun _ -> NavbarMsg.ToggleBurger |> Navbar |> Global |> dispatch)
                Navbar.Burger.IsActive(isDropDownOpen model.Burger)
            ] [ span [] []; span [] []; span [] [] ]
        ]
        Navbar.menu [
            Navbar.Menu.IsActive(isDropDownOpen model.Burger)
        ] [
            Navbar.Start.div [] (navbarStart dispatch model user)
            Navbar.End.div [] [
                helpMenu dispatch model

                accountMenu dispatch model user
            ]
        ]
    ]